// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { CondoStructuresService } from "../../condo-structures.service";
import { PropertyEditNoteDialogComponent } from "../document-edit-note-dialog/document-edit-note-dialog.component";

/**
 * Script start
 */

@Component({
  selector: "app-property-detail-mapping-note-dialog",
  templateUrl: "./mapping-note-dialog.component.html",
  styleUrls: ["./mapping-note-dialog.component.scss"]
})
export class PropertyDetailMappingNoteDialogComponent  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner
  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyDetailMappingNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    public confirmDialog: MatDialog,
    private condoStructuresService: CondoStructuresService,
    private helperService: HelperService,
    private toastrService: ToastrService,
    ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

  }
  
  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  
  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close();
  }

  
  /**
   * Handle edit
   *
   * @since 1.0.0
   */
   onEdit(
    text: string,
    noteId: string
  ): void {
    const editDialogRef = this.confirmDialog.open(
      PropertyEditNoteDialogComponent,
      {
        minWidth: "40%",
        maxHeight: "98vh",
        data: {
          title: this.translateService.instant(
            "properties.structures.notes"
          ),
          condoStructureId: this.data.condoStructureId,
          text,
          structureName: this.data.structureName ? this.data.structureName : "Anagrafica",
          structureId: this.data.structureId,
          noteId,
        },
        disableClose: true,
      }
    );
    editDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res || !res.status) {
          return;
        }
        const index = this.data.existingNotes.findIndex((el: any) => {
          return (
            el._id.toString() === noteId.toString()
          );
        });
        if (index > -1) {
          this.data.existingNotes[index].text = res.text;
        }
        return;
      });
  }

  /**
   * Handle delete
   *
   * @since 1.0.0
   */
  onDelete(noteId: string): void {
    const confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
      minWidth: "30%",
      maxHeight: "98vh",
      data: {
        title: this.translateService.instant("common.warnings.warning"),
        message: this.translateService.instant("common.warnings.noteDelete"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      disableClose: true,
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    confirmDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.condoStructuresService
            .deleteNote(
              this.data.condoStructureId,
              this.data.structureName,
              this.data.structureId,
              noteId
            )
            .pipe(take(1))
            .subscribe(
              (res) => {
                if (res.status) {
                  this.toastrService.success(res.message);
                  const index = this.data.existingNotes.findIndex((el: any) => {
                    return (
                      el._id.toString() === noteId.toString()
                    );
                  });
                  if (index > -1) {
                    this.data.existingNotes.splice(index, 1);
                  }
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
              }
            )
        );
        return;
      });
  }
}
